import { CreditCardVendors } from '@webapp/core/entities/external/CreditCardVendor'
import { type ExpenseItemFormSchema } from '@webapp/modules/expense/components/ExpenseLineItemDrawer'
import { ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import { AccountCodeTypes } from '@webapp/platform/finance'

const round2 = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100

function computeTaxAmount(item: ExpenseItemFormSchema): number {
  const total = Number.parseFloat(item.amount || '0')
  const taxValue = Number.parseFloat(item.taxValue || '0')

  switch (item.taxMode) {
    case 'percent': {
      const rate = taxValue / 100
      if (rate <= 0) return 0
      const tips =
        item.expenseTypeData?.type === 'MEALS'
          ? (item.expenseTypeData.data?.tips ?? 0)
          : 0
      const subtotal = (total - tips) / (1 + rate)
      return round2(subtotal * rate)
    }
    case 'dollar':
    case 'saved':
      return taxValue
    default:
      return 0
  }
}

export const mapExpenseItemFormSchemaToExpenseItem = (
  item: ExpenseItemFormSchema
): ExpenseItem => {
  return new ExpenseItem({
    id: item.id ?? 0,
    attachment: item.attachment || null,
    attachmentMimetype: item.attachmentMimetype || null,
    merchant: item.merchant,
    account: item.account
      ? {
          id: item.account.id,
          active: true,
          accountCode: {
            id: item.account.accountCode.id ?? 0,
            code: item.account.accountCode.code,
            description: item.account.accountCode.description,
            type: AccountCodeTypes.OTHER,
          },
          secondaryCategory: null,
        }
      : {
          id: 0,
          active: true,
          accountCode: {
            id: 0,
            code: '',
            description: '',
            type: AccountCodeTypes.OTHER,
          },
          secondaryCategory: null,
        },
    transactionDate: item.transactionDate || '',
    currency: item.currency,
    reimbursable: item.reimbursable,
    amount: Number.parseFloat(item.amount || '0'),
    taxAmount: computeTaxAmount(item),
    taxPercentage:
      item.taxMode === 'percent'
        ? String(Number.parseFloat(item.taxValue || '0') / 100)
        : item.taxMode === 'saved'
          ? item.taxPercentage
          : '0',
    taxName:
      item.taxMode === 'percent'
        ? '%'
        : item.taxMode === 'dollar'
          ? '$'
          : item.taxName,
    taxId: item.taxId ?? null,
    paymentCard:
      item.paymentMethod && item.paymentMethod.id > 0
        ? {
            id: item.paymentMethod.id,
            name: item.paymentMethod.name,
            last4Digits: item.paymentMethod.number,
            vendor: item.paymentMethod.type ?? CreditCardVendors.OTHER,
          }
        : null,
    notes: item.notes || '',
    expenseType: item.expenseType,
    paymentType: item.paymentType,
    expenseTypeData: item.expenseTypeData ?? { type: 'OTHER', data: null },
    mileageRatePresetId: item.mileageRatePreset?.id ?? null,
    mileageRatePresetName: item.mileageRatePreset?.name ?? null,
  })
}
